import { noop } from 'lodash-es';
import {
  forwardRef,
  type MouseEvent as ReactMouseEvent,
  type MouseEventHandler,
  type PropsWithChildren,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Menu } from '../../../../navigation/menu/Menu.js';
import { useResolveAsyncComponent } from '../../hooks/useResolveAsyncComponent.js';
import { renderMenuActionChildren } from '../../utils/render-menu-action-children.js';
import { CHART_SCROLL_IGNORE_ATTR } from '../overlay-tooltip/utils/is-scroll-ignored.js';

type ChartActionsMenuAsyncProps = {
  isOpen: boolean;
  defaultActions?: ReactNode;
  header?: ReactElement | null;
  onActionSelected?: MouseEventHandler<HTMLDivElement>;
  actions: (() => Promise<ReactElement>) | undefined;
  delay?: number;
  hasActions: boolean;
};

const ACTIONS_MENU_SIDE_OFFSET = 0;

type InternalMenuContentProps = ComponentProps<typeof Menu.Content> & {
  _sideOffset?: number;
};

export const ChartActionsMenuAsync = forwardRef<
  HTMLDivElement,
  PropsWithChildren<ChartActionsMenuAsyncProps>
>((props: PropsWithChildren<ChartActionsMenuAsyncProps>, forwardedRef) => {
  const { children, isOpen, hasActions } = props;
  if (!hasActions) {
    return children;
  }

  return (
    <Menu open={isOpen} modal={false}>
      <Menu.Trigger>{children}</Menu.Trigger>
      <Content {...props} ref={forwardedRef} />
    </Menu>
  );
});

const Content = forwardRef<HTMLDivElement, ChartActionsMenuAsyncProps>(
  (props, forwardedRef) => {
    const {
      isOpen,
      actions,
      defaultActions,
      onActionSelected,
      header,
      delay = 0,
    } = props;
    const [shouldRun, setShouldRun] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const customActions = useResolveAsyncComponent(actions, shouldRun);
    const defaultActionsMapped =
      shouldRun && defaultActions
        ? renderMenuActionChildren(defaultActions)
        : null;

    useEffect(() => {
      if (!isOpen) {
        return;
      }

      const timer = setTimeout(() => {
        setShouldRun(true);
      }, delay);

      return () => {
        clearTimeout(timer);
        setShouldRun(false);
      };
    }, [isOpen, delay]);

    if (!shouldRun) {
      return null;
    }

    const menuContentProps = {
      ref: forwardedRef,
      side: 'right',
      _sideOffset: ACTIONS_MENU_SIDE_OFFSET,
      [CHART_SCROLL_IGNORE_ATTR]: '',
    } as InternalMenuContentProps;

    const handleClick = (e: ReactMouseEvent<HTMLSpanElement>) => {
      const isSubmenuTrigger = (e.target as HTMLElement).closest(
        '[aria-haspopup="true"]',
      );
      if (!isSubmenuTrigger) {
        onActionSelected?.(e as unknown as ReactMouseEvent<HTMLDivElement>);
      }
    };

    return (
      <Menu.Content {...menuContentProps}>
        <div ref={ref} style={{ width: '100%' }} role="tooltip">
          <Menu.Group>{header}</Menu.Group>
          <span role="none" onClick={handleClick} onKeyDown={noop}>
            <Menu.Group>
              {defaultActionsMapped}
              {customActions &&
                renderMenuActionChildren(customActions.props.children, {
                  divideActionsAndIntents: false,
                })}
            </Menu.Group>
          </span>
        </div>
      </Menu.Content>
    );
  },
);
